import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { RefreshCw, Save, Wallet, Filter, ChevronRight, CheckCircle2, Clock, Download, Settings2, Mail, Landmark, MinusCircle } from 'lucide-react';

const REGIONS = ['USA', 'Canada', 'India', 'Other'];
const STATUSES = ['unpaid', 'approved', 'paid'];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const StatusPill = ({ status }) => {
  const cls = status === 'paid'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : status === 'approved'
    ? 'bg-blue-100 text-blue-700 border-blue-200'
    : 'bg-amber-100 text-amber-700 border-amber-200';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase border ${cls}`} data-testid={`status-${status}`}>{status}</span>;
};


// ── Summary tab ──────────────────────────────────────────────────────────────

const SummaryTab = ({ categories, onOpenTeacher }) => {
  const [preset, setPreset] = useState('month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [country, setCountry] = useState('all');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ preset });
      if (preset === 'custom') { params.set('start', start); params.set('end', end); }
      if (country !== 'all') params.set('country', country);
      if (category !== 'all') params.set('category', category);
      if (status !== 'all') params.set('status', status);
      const res = await api.get(`/admin/teacher-pay/summary?${params.toString()}`);
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error('Failed to load summary');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [preset, country, category, status]);

  // Distinct categories seen across all rows for column headers (compact view)
  const allCats = useMemo(() => {
    const s = new Set();
    rows.forEach(r => Object.keys(r.categories || {}).forEach(c => s.add(c)));
    return Array.from(s).sort();
  }, [rows]);

  const grand = useMemo(() => rows.reduce((a, r) => a + Number(r.total_payable || 0), 0), [rows]);
  const grandClasses = useMemo(() => rows.reduce((a, r) => a + Number(r.total_classes || 0), 0), [rows]);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3" data-testid="teacher-pay-filters">
        <div>
          <Label className="text-xs text-slate-500">Period</Label>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-36 h-9" data-testid="filter-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {preset === 'custom' && (
          <>
            <div><Label className="text-xs text-slate-500">From</Label><Input type="date" value={start} onChange={e => setStart(e.target.value)} className="w-36 h-9" data-testid="filter-start" /></div>
            <div><Label className="text-xs text-slate-500">To</Label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-36 h-9" data-testid="filter-end" /></div>
            <Button onClick={fetchSummary} className="h-9" data-testid="filter-apply">Apply</Button>
          </>
        )}
        <div>
          <Label className="text-xs text-slate-500">Country</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-32 h-9" data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44 h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-4 text-sm">
          <div><div className="text-xs text-slate-500">Total classes</div><div className="text-lg font-bold text-slate-900 v2-num">{grandClasses}</div></div>
          <div><div className="text-xs text-slate-500">Total payable</div><div className="text-lg font-bold text-emerald-700 v2-num">{money(grand)}</div></div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="teacher-pay-summary-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs">Teacher</TableHead>
              <TableHead className="text-xs">Country</TableHead>
              {allCats.map(c => <TableHead key={c} className="text-xs text-center">{c}</TableHead>)}
              <TableHead className="text-xs text-center">Total</TableHead>
              <TableHead className="text-xs text-right">Payable</TableHead>
              <TableHead className="text-xs"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={allCats.length + 5} className="text-center py-8 text-slate-500">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={allCats.length + 5} className="text-center py-8 text-slate-500">No completed classes in this period.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.teacher_id} className="hover:bg-slate-50" data-testid={`teacher-pay-row-${r.teacher_id}`}>
                <TableCell className="font-semibold text-slate-900">{r.teacher_name}</TableCell>
                <TableCell><span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">{r.country}</span></TableCell>
                {allCats.map(c => {
                  const cell = r.categories[c];
                  return <TableCell key={c} className="text-center text-sm">
                    {cell ? <><span className="font-bold v2-num">{cell.count}</span><span className="text-slate-400 text-[11px]"> · {money(cell.subtotal)}</span></> : <span className="text-slate-300">–</span>}
                  </TableCell>;
                })}
                <TableCell className="text-center font-bold text-slate-900 v2-num">{r.total_classes}</TableCell>
                <TableCell className="text-right font-bold text-emerald-700 v2-num">{money(r.total_payable)}</TableCell>
                <TableCell><Button size="sm" variant="ghost" className="h-8" onClick={() => onOpenTeacher(r)} data-testid={`open-teacher-${r.teacher_id}`}>Details <ChevronRight className="w-3 h-3 ml-1" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};


// ── Rates tab ────────────────────────────────────────────────────────────────

const RatesTab = ({ categories, onRefresh }) => {
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchRates = async () => {
    try {
      const res = await api.get('/admin/teacher-pay/rates');
      setMatrix(res.data.matrix || {});
    } catch { toast.error('Failed to load rates'); }
  };
  useEffect(() => { fetchRates(); }, []);

  const setCell = (cat, region, val) => {
    const n = Math.max(0, Number(val) || 0);
    setMatrix(m => ({ ...m, [cat]: { ...(m[cat] || {}), [region]: n } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/teacher-pay/rates', { matrix });
      toast.success('Rates updated');
      onRefresh?.();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const cats = Array.from(new Set([...(categories || []), ...Object.keys(matrix)])).sort();

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="rates-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-slate-900">Pay Rate Matrix</h3>
          <p className="text-xs text-slate-500">₹ per completed class. Changes apply only to new & unpaid records — approved / paid entries keep their historical rate.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-rates-btn">
          {saving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save Rates</>}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left text-xs text-slate-600 font-bold px-3 py-2">Category</th>
              {REGIONS.map(r => <th key={r} className="text-center text-xs text-slate-600 font-bold px-3 py-2">{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {cats.map(c => (
              <tr key={c} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-semibold text-slate-900">{c}</td>
                {REGIONS.map(r => (
                  <td key={r} className="px-2 py-2">
                    <Input type="number" min={0}
                      value={(matrix[c] || {})[r] ?? ''}
                      onChange={e => setCell(c, r, e.target.value)}
                      className="w-24 h-8 text-center v2-num"
                      data-testid={`rate-${c}-${r}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ── Teacher detail drawer ────────────────────────────────────────────────────

const TeacherDetail = ({ teacherRow, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [showOverride, setShowOverride] = useState(false);
  const [overrideMatrix, setOverrideMatrix] = useState({});
  const [savingOverride, setSavingOverride] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [showDeductions, setShowDeductions] = useState(false);
  const [ded, setDed] = useState({ tds: 0, other: 0, bonuses: 0, notes: '', payment_date: '' });
  const [savingDed, setSavingDed] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [bank, setBank] = useState({ upi_id: '', bank_account: '', bank_ifsc: '', bank_name: '', account_holder: '' });
  const [savingBank, setSavingBank] = useState(false);

  const load = async () => {
    if (!teacherRow) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/teacher-pay/teacher/${teacherRow.teacher_id}?preset=all`);
      setData(res.data);
    } catch { toast.error('Failed to load detail'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [teacherRow?.teacher_id]);

  const loadOverride = async () => {
    try {
      const res = await api.get(`/admin/teacher-pay/overrides/${teacherRow.teacher_id}`);
      setOverrideMatrix(res.data.matrix || {});
    } catch {}
  };
  const openOverride = async () => { await loadOverride(); setShowOverride(true); };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      // Strip zero/empty cells so blanks fall back to the global matrix.
      const clean = {};
      Object.entries(overrideMatrix).forEach(([cat, row]) => {
        const rowClean = {};
        Object.entries(row || {}).forEach(([reg, val]) => {
          const n = Number(val);
          if (n > 0) rowClean[reg] = n;
        });
        if (Object.keys(rowClean).length) clean[cat] = rowClean;
      });
      await api.put(`/admin/teacher-pay/overrides/${teacherRow.teacher_id}`, { matrix: clean });
      toast.success('Override saved. Run Backfill to reprice unpaid records.');
      setShowOverride(false);
    } catch { toast.error('Save failed'); }
    finally { setSavingOverride(false); }
  };

  const setOvCell = (cat, reg, val) => setOverrideMatrix(m => ({
    ...m, [cat]: { ...(m[cat] || {}), [reg]: val === '' ? '' : Number(val) },
  }));

  // Deductions
  const openDeductions = async () => {
    try {
      const res = await api.get(`/admin/teacher-pay/deductions/${teacherRow.teacher_id}?month=${month}`);
      setDed({
        tds: res.data.tds || 0, other: res.data.other || 0, bonuses: res.data.bonuses || 0,
        notes: res.data.notes || '', payment_date: res.data.payment_date || '',
      });
    } catch { setDed({ tds: 0, other: 0, bonuses: 0, notes: '', payment_date: '' }); }
    setShowDeductions(true);
  };
  const saveDeductions = async () => {
    setSavingDed(true);
    try {
      await api.put(`/admin/teacher-pay/deductions/${teacherRow.teacher_id}?month=${month}`, ded);
      toast.success('Deductions saved for ' + month);
      setShowDeductions(false);
    } catch { toast.error('Save failed'); }
    finally { setSavingDed(false); }
  };

  // Bank details
  const openBank = async () => {
    try {
      const res = await api.get(`/admin/teacher-pay/bank/${teacherRow.teacher_id}`);
      setBank({
        upi_id: res.data.upi_id || '', bank_account: res.data.bank_account || '',
        bank_ifsc: res.data.bank_ifsc || '', bank_name: res.data.bank_name || '',
        account_holder: res.data.account_holder || '',
      });
    } catch { setBank({ upi_id: '', bank_account: '', bank_ifsc: '', bank_name: '', account_holder: '' }); }
    setShowBank(true);
  };
  const saveBank = async () => {
    setSavingBank(true);
    try {
      await api.put(`/admin/teacher-pay/bank/${teacherRow.teacher_id}`, bank);
      toast.success('Payment details saved');
      setShowBank(false);
    } catch { toast.error('Save failed'); }
    finally { setSavingBank(false); }
  };

  // Email payslip
  const emailPayslip = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/admin/teacher-pay/payslip/${teacherRow.teacher_id}/email`, { month });
      toast.success(`Payslip sent to ${res.data.sent_to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Email failed';
      toast.error(typeof msg === 'string' ? msg : 'Email failed');
    } finally { setEmailing(false); }
  };

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => (s.size === (data?.records || []).length ? new Set() : new Set((data?.records || []).map(r => r.id))));

  const mark = async (status, opts = {}) => {
    if (selected.size === 0) { toast.info('Select rows first'); return; }
    try {
      const body = { ids: Array.from(selected), status };
      if (opts.emailPayslip) body.email_payslip = true;
      const res = await api.patch('/admin/teacher-pay/records/status', body);
      const sent = (res.data.emailed || []).filter(e => e.status === 'sent').length;
      toast.success(`Marked ${selected.size} as ${status}${sent ? ` · emailed ${sent} payslip(s)` : ''}`);
      setSelected(new Set());
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Update failed';
      toast.error(typeof msg === 'string' ? msg : 'Update failed');
    }
  };

  const downloadPayslip = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/teacher-pay/payslip/${teacherRow.teacher_id}/html?month=${month}`, {
        responseType: 'text',
        transformResponse: [(d) => d],  // keep as raw string, don't JSON.parse
      });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Popup blocked — allow popups to view payslip.');
      // Revoke after a delay so the tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch { toast.error('Payslip failed'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={!!teacherRow} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl bg-white p-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="teacher-detail-dialog">
        <DialogHeader className="p-5 border-b border-slate-200">
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap">
            <Wallet className="w-5 h-5 text-emerald-600" />
            {teacherRow?.teacher_name} <span className="text-sm font-normal text-slate-500">· {teacherRow?.country}</span>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-40 h-8" data-testid="payslip-month" />
              <Button size="sm" onClick={downloadPayslip} disabled={downloading} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="download-payslip-btn">
                {downloading ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Payslip
              </Button>
              <Button size="sm" onClick={emailPayslip} disabled={emailing} className="bg-violet-600 hover:bg-violet-700 h-8" data-testid="email-payslip-btn">
                {emailing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />} Email
              </Button>
              <Button size="sm" onClick={openDeductions} variant="outline" className="h-8" data-testid="open-deductions-btn">
                <MinusCircle className="w-3.5 h-3.5 mr-1" /> Deductions
              </Button>
              <Button size="sm" onClick={openBank} variant="outline" className="h-8" data-testid="open-bank-btn">
                <Landmark className="w-3.5 h-3.5 mr-1" /> Bank
              </Button>
              <Button size="sm" onClick={openOverride} variant="outline" className="h-8" data-testid="open-override-btn">
                <Settings2 className="w-3.5 h-3.5 mr-1" /> Rates
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 p-5 bg-slate-50 border-b border-slate-200">
              <div><div className="text-xs text-slate-500">Total Classes</div><div className="text-2xl font-bold text-slate-900 v2-num">{data.total_classes}</div></div>
              <div><div className="text-xs text-slate-500">Total Payable</div><div className="text-2xl font-bold text-emerald-700 v2-num">{money(data.total_payable)}</div></div>
              <div><div className="text-xs text-slate-500">Unpaid / Approved</div><div className="text-lg font-bold text-amber-700 v2-num">{money(data.unpaid_total)} · {money(data.approved_total)}</div></div>
              <div><div className="text-xs text-slate-500">Paid</div><div className="text-2xl font-bold text-blue-700 v2-num">{money(data.paid_total)}</div></div>
            </div>
            <div className="p-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500">{selected.size} selected</span>
              <Button size="sm" onClick={() => mark('approved')} className="bg-blue-600 hover:bg-blue-700 h-8" disabled={selected.size === 0} data-testid="mark-approved-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" onClick={() => mark('paid', { emailPayslip: true })} className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={selected.size === 0} data-testid="mark-paid-btn"><Wallet className="w-3.5 h-3.5 mr-1" /> Mark Paid + Email</Button>
              <Button size="sm" onClick={() => mark('unpaid')} variant="outline" className="h-8" disabled={selected.size === 0} data-testid="mark-unpaid-btn"><Clock className="w-3.5 h-3.5 mr-1" /> Reset</Button>
            </div>
            <div className="overflow-y-auto flex-1">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-8"><input type="checkbox" checked={selected.size === data.records.length && data.records.length > 0} onChange={toggleAll} data-testid="select-all-check" /></TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Student</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs">Country</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    <TableHead className="text-xs text-right">Pay</TableHead>
                    <TableHead className="text-xs text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.records.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No completed classes.</TableCell></TableRow>}
                  {data.records.map(r => (
                    <TableRow key={r.id} className="hover:bg-slate-50">
                      <TableCell><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`row-check-${r.id}`} /></TableCell>
                      <TableCell className="text-xs">{r.date}</TableCell>
                      <TableCell className="text-xs">{r.student_name || '—'}</TableCell>
                      <TableCell className="text-xs">{r.pay_category || '—'}</TableCell>
                      <TableCell className="text-xs">{r.teacher_region || '—'}</TableCell>
                      <TableCell className="text-xs text-right v2-num">{money(r.applied_rate || r.amount_inr)}</TableCell>
                      <TableCell className="text-xs text-right font-bold v2-num">{money(r.amount_inr)}</TableCell>
                      <TableCell className="text-center"><StatusPill status={r.payment_status || 'unpaid'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Override matrix dialog — nested */}
        {showOverride && (
          <div className="absolute inset-0 bg-black/40 z-20 flex items-center justify-center p-6" onClick={() => setShowOverride(false)}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl p-5 max-w-2xl w-full" onClick={e => e.stopPropagation()} data-testid="override-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-slate-900">Per-Teacher Rate Override</h3>
                  <p className="text-xs text-slate-500">Leave a cell blank/zero to fall back to the global matrix. Only new & unpaid records will pick this up.</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowOverride(false)}>Close</Button>
              </div>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left text-xs px-3 py-2 font-bold">Category</th>
                      {['USA','Canada','India','Other'].map(r => <th key={r} className="text-center text-xs px-3 py-2 font-bold">{r}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {['Coding','GenAI','Science','Robotics','Mathematics','Spoken Mastery','Chess','IELTS&PTE'].map(c => (
                      <tr key={c} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-semibold text-slate-900 text-sm">{c}</td>
                        {['USA','Canada','India','Other'].map(r => (
                          <td key={r} className="px-2 py-2">
                            <Input type="number" min={0}
                              value={(overrideMatrix[c] || {})[r] ?? ''}
                              onChange={e => setOvCell(c, r, e.target.value)}
                              className="w-20 h-8 text-center v2-num"
                              placeholder="—"
                              data-testid={`override-${c}-${r}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowOverride(false)}>Cancel</Button>
                <Button onClick={saveOverride} disabled={savingOverride} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-override-btn">
                  {savingOverride ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" /> Save Override</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Deductions panel — nested */}
        {showDeductions && (
          <div className="absolute inset-0 bg-black/40 z-20 flex items-center justify-center p-6" onClick={() => setShowDeductions(false)}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl p-5 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="deductions-panel">
              <h3 className="font-bold text-slate-900 flex items-center gap-2"><MinusCircle className="w-4 h-4 text-rose-500" /> Deductions · {month}</h3>
              <p className="text-xs text-slate-500 mb-3">Applied to this teacher's payslip for the selected month.</p>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div><Label className="text-xs">Tax / TDS (₹)</Label><Input type="number" min={0} value={ded.tds} onChange={e => setDed(d => ({ ...d, tds: Number(e.target.value) || 0 }))} data-testid="ded-tds" /></div>
                <div><Label className="text-xs">Other (₹)</Label><Input type="number" min={0} value={ded.other} onChange={e => setDed(d => ({ ...d, other: Number(e.target.value) || 0 }))} data-testid="ded-other" /></div>
                <div><Label className="text-xs">Bonus (₹)</Label><Input type="number" min={0} value={ded.bonuses} onChange={e => setDed(d => ({ ...d, bonuses: Number(e.target.value) || 0 }))} data-testid="ded-bonus" /></div>
              </div>
              <div className="mb-3"><Label className="text-xs">Payment Date (optional)</Label><Input type="text" placeholder="e.g. 05 September 2026" value={ded.payment_date} onChange={e => setDed(d => ({ ...d, payment_date: e.target.value }))} data-testid="ded-paydate" /></div>
              <div className="mb-3"><Label className="text-xs">Notes (optional)</Label><Input type="text" value={ded.notes} onChange={e => setDed(d => ({ ...d, notes: e.target.value }))} data-testid="ded-notes" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeductions(false)}>Cancel</Button>
                <Button onClick={saveDeductions} disabled={savingDed} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-deductions-btn">
                  {savingDed ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" /> Save Deductions</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bank panel — nested */}
        {showBank && (
          <div className="absolute inset-0 bg-black/40 z-20 flex items-center justify-center p-6" onClick={() => setShowBank(false)}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl p-5 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="bank-panel">
              <h3 className="font-bold text-slate-900 flex items-center gap-2"><Landmark className="w-4 h-4 text-blue-600" /> Payment Details</h3>
              <p className="text-xs text-slate-500 mb-3">Appears on every payslip's Payment Details section.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Account Holder Name</Label><Input value={bank.account_holder} onChange={e => setBank(b => ({ ...b, account_holder: e.target.value }))} data-testid="bank-holder" /></div>
                <div className="col-span-2"><Label className="text-xs">UPI ID</Label><Input placeholder="name@bank" value={bank.upi_id} onChange={e => setBank(b => ({ ...b, upi_id: e.target.value }))} data-testid="bank-upi" /></div>
                <div><Label className="text-xs">Bank Name</Label><Input value={bank.bank_name} onChange={e => setBank(b => ({ ...b, bank_name: e.target.value }))} data-testid="bank-name" /></div>
                <div><Label className="text-xs">IFSC</Label><Input value={bank.bank_ifsc} onChange={e => setBank(b => ({ ...b, bank_ifsc: e.target.value }))} data-testid="bank-ifsc" /></div>
                <div className="col-span-2"><Label className="text-xs">Bank Account Number</Label><Input value={bank.bank_account} onChange={e => setBank(b => ({ ...b, bank_account: e.target.value }))} data-testid="bank-account" /></div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowBank(false)}>Cancel</Button>
                <Button onClick={saveBank} disabled={savingBank} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-bank-btn">
                  {savingBank ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" /> Save Details</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ── Root component ───────────────────────────────────────────────────────────

const TeacherPayDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/admin/teacher-pay/rates');
      setCategories(res.data.categories || []);
    } catch {}
  };
  useEffect(() => { fetchCategories(); }, []);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await api.post('/admin/teacher-pay/backfill');
      toast.success(`Created ${res.data.created}, refreshed ${res.data.refreshed}, enriched ${res.data.enriched}`);
    } catch { toast.error('Backfill failed'); }
    finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-4" data-testid="teacher-pay-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Teacher Pay</h1>
          <p className="text-slate-500 font-semibold">Per-class earnings by category × country</p>
        </div>
        <Button onClick={runBackfill} disabled={backfilling} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="backfill-btn">
          {backfilling ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Backfilling…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Backfill from Completed Classes</>}
        </Button>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="summary" data-testid="tab-summary"><Filter className="w-4 h-4 mr-1" /> Summary</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates"><Save className="w-4 h-4 mr-1" /> Rates</TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="mt-4">
          <SummaryTab categories={categories} onOpenTeacher={setSelectedTeacher} />
        </TabsContent>
        <TabsContent value="rates" className="mt-4">
          <RatesTab categories={categories} onRefresh={fetchCategories} />
        </TabsContent>
      </Tabs>

      <TeacherDetail teacherRow={selectedTeacher} onClose={() => setSelectedTeacher(null)} />
    </div>
  );
};

export default TeacherPayDashboard;
